function normalizePrescriptionFieldKey(key) {
  if (!key) return '';
  const normalized = String(key).toUpperCase();
  if (normalized === 'PD_R') return 'OD_PD';
  if (normalized === 'PD_L') return 'OS_PD';
  return normalized;
}

function collectPrescriptionKeysFromLines(lines) {
  return [...new Set(
      Array.from(lines || [])
          .map((line) => line?.dataset?.prescriptionKey)
          .filter(Boolean)
  )];
}

async function updateCartAttributes(attributes) {
  const response = await fetch('/cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ attributes }),
  });

  if (!response.ok) {
    throw new Error(`Cart attribute update failed (${response.status})`);
  }

  return response.json();
}

async function clearPrescriptionCartAttributes(keys) {
  const uniqueKeys = [...new Set((keys || []).filter(Boolean))];
  if (!uniqueKeys.length) return;

  const attributes = {};
  uniqueKeys.forEach((key) => {
    attributes[key] = '';
  });

  try {
    await updateCartAttributes(attributes);
  } catch (error) {
    console.warn('Failed to clear prescription cart attributes:', error);
  }
}

function parsePrescriptionAttribute(raw) {
  if (!raw) return null;

  try {
    const data = JSON.parse(raw);
    return data && typeof data === 'object' ? data : null;
  } catch (error) {
    console.warn('Failed to parse prescription cart attribute:', error);
    return null;
  }
}

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', async (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      if (this.getAttribute('data-properties-prescription-id')) {
        const allPrescriptions = document.querySelectorAll(
            `tr[data-properties-prescription-id="${this.closest('.cart-item').dataset.propertiesPrescriptionId}"]`
        );

        await cartItems.updateQuantityLines(allPrescriptions);
      } else {
        await cartItems.updateQuantity(this.dataset.index, 0).then(res => {});
      }
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
        document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }

      const cartFooterWrapperEl = document.querySelector('.cart__footer-wrapper');
      const cartFooterSectionEl = document.getElementById('main-cart-footer');

      cartFooterWrapperEl?.style.removeProperty('display');
      cartFooterSectionEl?.style.removeProperty('display');

      this.onCartUpdate();
    });
    this.watchCartContent();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    if (!id) return;
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    if (event.target.classList.contains('no-js')) return;

    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue === 0) {
      this.updateQuantity(index, 0, document.activeElement.getAttribute('name'), event.target.dataset.quantityVariantId);
      return;
    }

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(index, inputValue, document.activeElement.getAttribute('name'), event.target.dataset.quantityVariantId);
    }
  }


  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
          .then(async (response) => {
            if (!response.ok) throw new Error(`Cart drawer refresh failed: HTTP ${response.status}`);
            return response.text();
          })
          .then(async (responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
            for (const selector of selectors) {
              const targetElement = document.querySelector(selector);
              const sourceElement = html.querySelector(selector);
              if (targetElement && sourceElement) {
                targetElement.replaceWith(sourceElement);
              }
            }
            window.prescriptionModal.init();
            const cartGeneration = window.customAddToCart?.cartStateGeneration;
            const cart = await window.customAddToCart?.getCart();
            if (cart) await window.customAddToCart.giftLineInit(cart.total_price, false, cart, cartGeneration);
          })
          .catch((e) => {
            console.error(e);
          });
    } else {
      fetch(routes.cart_url)
          .then(async (response) => {
            if (!response.ok) throw new Error(`Cart page refresh failed: HTTP ${response.status}`);
            return response.text();
          })
          .then(async (responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');

            const sourceCartItems = html.querySelector('cart-items');
            const targetCartItems = document.querySelector('cart-items');

            if (sourceCartItems && targetCartItems) {
              targetCartItems.innerHTML = sourceCartItems.innerHTML;
              targetCartItems.className = sourceCartItems.className;
              targetCartItems.removeAttribute('style');
            }

            const sourceFooter = html.querySelector('.cart__footer-wrapper');
            const targetFooter = document.querySelector('.cart__footer-wrapper');

            if (sourceFooter && targetFooter) {
              targetFooter.replaceWith(sourceFooter);
            }
            this.restoreCartFooter();
            window.prescriptionModal.init();
            const cartGeneration = window.customAddToCart?.cartStateGeneration;
            const cart = await window.customAddToCart?.getCart();
            if (cart) await window.customAddToCart.giftLineInit(cart.total_price, false, cart, cartGeneration);
          })
          .catch((e) => {
            console.error(e);
          });
    }
  }
  restoreCartFooter() {
    const cartFooterWrapperEl = document.querySelector('.cart__footer-wrapper');
    const cartFooterSectionEl = document.getElementById('main-cart-footer');

    cartFooterWrapperEl?.style.removeProperty('display');
    cartFooterSectionEl?.style.removeProperty('display');
  }
  watchCartContent() {
    const cartItemsRoot = this;
    if (!cartItemsRoot) return;

    if (this.cartContentObserver) {
      this.cartContentObserver.disconnect();
    }

    this.cartContentObserver = new MutationObserver(() => {
      const hasCartItems = !!cartItemsRoot.querySelector('.cart-item');
      const progressBarEl = document.querySelector('cart-items .box-progress-bar');
      const cartFooterWrapperEl = document.querySelector('.cart__footer-wrapper');
      const cartFooterSectionEl = document.getElementById('main-cart-footer');

      if (hasCartItems) {
        progressBarEl?.style.removeProperty('display');
        cartFooterWrapperEl?.style.removeProperty('display');
        cartFooterSectionEl?.style.removeProperty('display');
      }
    });

    this.cartContentObserver.observe(cartItemsRoot, {
      childList: true,
      subtree: true,
    });
  }
  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  async updateQuantityLines(lines) {
    const prescriptionKeys = collectPrescriptionKeysFromLines(lines);

    Array.from(lines).forEach((line) => {
      this.enableLoading(line.dataset.index);
    });

    const updates = Object.fromEntries(Array.from(lines).map((line) => [line.dataset.key, 0]));

    try {
      const cartGeneration = window.customAddToCart?.beginCartMutation?.();
      const url = '/cart/update.js';
      const options = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          updates,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname,
        }),
      };

      const response = await fetch(url, options);

      if (response.ok) {
        const parsedState = await response.json();
        const progressBarEl = document.querySelector('cart-items .box-progress-bar');
        const cartFooterWrapperEl = document.querySelector('.cart__footer-wrapper');
        const cartFooterSectionEl = document.getElementById('main-cart-footer');

        const isEmptyCart = parsedState.item_count === 0;

        if (isEmptyCart) {
          progressBarEl?.style.setProperty('display', 'none', 'important');
          cartFooterWrapperEl?.style.setProperty('display', 'none', 'important');
          cartFooterSectionEl?.style.setProperty('display', 'none', 'important');
        } else {
          progressBarEl?.style.removeProperty('display');
          cartFooterWrapperEl?.style.removeProperty('display');
          cartFooterSectionEl?.style.removeProperty('display');
        }

        Array.from(lines).forEach((line) => {
          this.disableLoading(line.dataset.index);
        });

        // Section refreshes return the server-rendered legacy progress markup.
        // Reapply the active pair-promo state to the newly injected DOM before
        // publishing the cart update so the drawer/page cannot flash legacy UI.
        if (window.customAddToCart?.giftLineInit) {
          // Isolated on purpose: this call sits inside Dawn's try, whose catch tells the shopper
          // "there was an error updating your cart". A progress-bar refresh that fails must not
          // impersonate a failed cart update.
          try {
            await window.customAddToCart.giftLineInit(parsedState.total_price, false, parsedState, cartGeneration);
          } catch (promoError) {
            console.error('pair promo refresh failed', promoError);
          }
        }
        await clearPrescriptionCartAttributes(prescriptionKeys);

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: parsedState
        });

        return parsedState;
      }
    } catch (error) {
      console.log('updateQuantityLines error: ', error);
    }

    return;
  }

  async updateQuantity(line, quantity, name, variantId) {
    const lineItemBeforeUpdate =
        document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
    const prescriptionKey = Number(quantity) === 0 ? lineItemBeforeUpdate?.dataset?.prescriptionKey : '';

    this.enableLoading(line);

    try {
      const cartGeneration = window.customAddToCart?.beginCartMutation?.();
      const url = `${routes.cart_change_url}`;
      const body = JSON.stringify({
        line,
        quantity,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });

      const response = await fetch(url, { ...fetchConfig(), ...{ body } });
      if (response.ok) {
        const state = await response.text();
        const parsedState = JSON.parse(state);
        const progressBarEl = document.querySelector('cart-items .box-progress-bar');
        const cartFooterWrapperEl = document.querySelector('.cart__footer-wrapper');
        const cartFooterSectionEl = document.getElementById('main-cart-footer');

        const isEmptyCart = parsedState.item_count === 0;

        if (isEmptyCart) {
          progressBarEl?.style.setProperty('display', 'none', 'important');
          cartFooterWrapperEl?.style.setProperty('display', 'none', 'important');
          cartFooterSectionEl?.style.setProperty('display', 'none', 'important');
        } else {
          progressBarEl?.style.removeProperty('display');
          cartFooterWrapperEl?.style.removeProperty('display');
          cartFooterSectionEl?.style.removeProperty('display');
        }
        const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector,
          );
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        // The cart endpoint re-renders the section from Liquid. In pair-promo
        // mode that response starts with legacy bars hidden by the initial
        // script, so restore the active pair state after replacing the HTML.
        if (window.customAddToCart?.giftLineInit) {
          // Isolated on purpose: this call sits inside Dawn's try, whose catch tells the shopper
          // "there was an error updating your cart". A progress-bar refresh that fails must not
          // impersonate a failed cart update.
          try {
            await window.customAddToCart.giftLineInit(parsedState.total_price, false, parsedState, cartGeneration);
          } catch (promoError) {
            console.error('pair promo refresh failed', promoError);
          }
        }
        await clearPrescriptionCartAttributes([prescriptionKey]);

        const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });

        sessionStorage.setItem('cartChanged', 'true');
        this.disableLoading(line);
      }

    } catch(error) {
      this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
      const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
      errors.textContent = window.cartStrings.error;

      console.log('updateQuantity error: ', error);
      return error;
    }
  }

  updateLiveRegions(line, message) {
    const lineItemError =
        document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
        document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);
window.cartItem = new CartItems();
window.restoreCartFooterVisibility = function () {
  document.querySelector('.cart__footer-wrapper')?.style.removeProperty('display');
  document.getElementById('main-cart-footer')?.style.removeProperty('display');
};
if (!customElements.get('cart-note')) {
  customElements.define(
      'cart-note',
      class CartNote extends HTMLElement {
        constructor() {
          super();

          this.addEventListener(
              'input',
              debounce((event) => {
                const body = JSON.stringify({ note: event.target.value });
                fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
              }, ON_CHANGE_DEBOUNCE_TIMER)
          );
        }
      }
  );
}
class CustomAddToCart {
  constructor() {
    this.drawer = document.querySelector('.cart-drawer');
    this.rate = +Shopify.currency.rate;
    this.country = Shopify.country.toLowerCase();

    this.pairPromoMode = window.ProductBundlerOptionsConfig.pairPromoMode || 'legacy';
    // Normalize allowlist entries: metafield list items (or a single comma-joined
    // fallback string) must become discrete lowercased product types.
    this.pairQualifyingTypes = (window.ProductBundlerOptionsConfig.pairQualifyingTypes || [])
        .flatMap((type) => String(type).split(','))
        .map((type) => type.trim().toLowerCase())
        .filter(Boolean);
    this.pairPromoTexts = [
      window.ProductBundlerOptionsConfig.pairPromoText0 || '',
      window.ProductBundlerOptionsConfig.pairPromoText1 || '',
      window.ProductBundlerOptionsConfig.pairPromoText2 || '',
    ];
    this.pairPromoRenderSequence = 0;
    this.cartStateGeneration = 0;

    this.enable_card = window.ProductBundlerOptionsConfig.enable_card;
    this.giftProductID = window.ProductBundlerOptionsConfig.cartGiftProduct;
    this.textCartFreeGiftProductText = window.ProductBundlerOptionsConfig.cartFreeGiftProductText;
    this.cartPrescriptionText = window.ProductBundlerOptionsConfig.cartPrescriptionText;
    this.giftProductUsPrise = window.ProductBundlerOptionsConfig.cartGiftProductUsPrise;
    this.giftProductInternationalPrise = window.ProductBundlerOptionsConfig.cartGiftProductInternationalPrise;
    this.cartFreeShippingInternationalSpend = window.ProductBundlerOptionsConfig.cartFreeShippingInternationalSpend;
    this.cartFreeExpressShippingUsSpend = window.ProductBundlerOptionsConfig.cartFreeExpressShippingUsSpend;
    this.expressShippingPrice = this.country === 'us' ? this.cartFreeExpressShippingUsSpend : Infinity;
    this.cartFreeShippingUsSpend = window.ProductBundlerOptionsConfig.cartFreeShippingUsSpend;
    this.cartFreeShippingPromoEnable = window.ProductBundlerOptionsConfig.cartFreeShippingPromoEnable;

    this.isFreeShippingEnabled =
        !!this.cartFreeShippingPromoEnable &&
        (
            this.country === 'us'
                ? Number(this.cartFreeShippingUsSpend) > 0
                : Number(this.cartFreeShippingInternationalSpend) > 0
        );
    this.cartFreeShippingInternationalSpend = window.ProductBundlerOptionsConfig.cartFreeShippingInternationalSpend;
    this.cartFreeShippingUsText = window.ProductBundlerOptionsConfig.cartFreeShippingUsText;
    this.free_two_days_shipping_price_rx_us =
        window.ProductBundlerOptionsConfig.free_two_days_shipping_price_rx_us;

    this.free_two_days_shipping_price_rx_int =
        window.ProductBundlerOptionsConfig.free_two_days_shipping_price_rx_int;

    this.free_two_days_shipping_enable = window.ProductBundlerOptionsConfig.free_two_days_shipping_enable;
    this.free_two_days_shipping_normal_enable = window.ProductBundlerOptionsConfig.free_two_days_shipping_normal_enable;
    this.free_two_days_shipping_price_us = window.ProductBundlerOptionsConfig.free_two_days_shipping_price_us;
    this.free_two_days_shipping_price_int = window.ProductBundlerOptionsConfig.free_two_days_shipping_price_int;
    this.freeTwoDaysShippingPrice =
        this.country === 'us' ? this.free_two_days_shipping_price_us : this.free_two_days_shipping_price_int * this.rate;

    this.differentGiftProductID = window.ProductBundlerOptionsConfig.cart_different_gift_product;
    this.different_product_price_us = window.ProductBundlerOptionsConfig.different_product_price_us;
    this.different_product_price_int = window.ProductBundlerOptionsConfig.different_product_price_int;
    this.freeTwoDaysShippingText =
        window.ProductBundlerOptionsConfig.free_two_days_shipping_text || 'FREE 2-DAY SHIPPING';
    this.free_two_days_shipping_price_us = window.ProductBundlerOptionsConfig.free_two_days_shipping_price_us;
    this.free_two_days_shipping_price_int = window.ProductBundlerOptionsConfig.free_two_days_shipping_price_int;
    this.freeTwoDaysShippingPrice =
        this.country === 'us' ? this.free_two_days_shipping_price_us : this.free_two_days_shipping_price_int * this.rate;

    this.isUS = this.country === 'us';

    this.cartShippingPrice = this.isUS
        ? this.cartFreeShippingUsSpend
        : this.cartFreeShippingInternationalSpend * this.rate;

    this.cartFreeExpressShippingUsSpend = window.ProductBundlerOptionsConfig.cartFreeExpressShippingUsSpend;
    this.expressEnabled = window.ProductBundlerOptionsConfig.cartFreeExpressShippingEnable;

    this.expressShippingPrice = this.isUS ? this.cartFreeExpressShippingUsSpend : Infinity;
    this.cartFreeShippingPromoEnable = window.ProductBundlerOptionsConfig.cartFreeShippingPromoEnable;
    this.giftPrice = this.country === 'us' ? this.giftProductUsPrise : this.giftProductInternationalPrise * this.rate;
    this.cartShippingPrice =
        this.country === 'us' ? this.cartFreeShippingUsSpend : this.cartFreeShippingInternationalSpend * this.rate;

    this.diferentPrice =
        this.country === 'us' ? this.different_product_price_us : this.different_product_price_int * this.rate;

    this.cartInit();
  }

  async cartInit() {

    this.observeCartItems();
    const cartGeneration = this.cartStateGeneration;
    const cart = await this.getCart();

    if (cart) {
      this.giftLineInit(cart.total_price, false, cart, cartGeneration);
    }

  }

  beginCartMutation() {
    this.cartStateGeneration += 1;
    return this.cartStateGeneration;
  }

  async getCart() {
    try {
      const timestamp = Date.now();
      const response = await fetch(`/cart.js?t=${timestamp}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const cart = await response.json();

      document.querySelector('.total_items').textContent = `(${cart.item_count})`;

      return cart;
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }
  getFree2DayPrice(hasPrescriptionProduct) {
    if (hasPrescriptionProduct) {
      return this.country === 'us'
          ? this.free_two_days_shipping_price_rx_us
          : this.free_two_days_shipping_price_rx_int * this.rate;
    }

    return this.country === 'us'
        ? this.free_two_days_shipping_price_us
        : this.free_two_days_shipping_price_int * this.rate;
  }
  async containsPrescriptionProduct(cart) {
    if (!cart || !cart.items || cart.items.length <= 0) return false;

    const nonGiftItems = cart.items.filter((item) =>
        !(item.properties && '_gift_product' in item.properties) &&
        !item.gift_card
    );

    if (nonGiftItems.length === 0) return false;

    const results = await Promise.all(
        nonGiftItems.map((item) =>
            fetch(`/products/${item.handle}.js`)
                .then((res) => (res.ok ? res.json() : null))
                .then((product) => {
                  if (!product) return false;
                  return product.type?.toLowerCase().includes('prescription');
                })
                .catch(() => false),
        ),
    );

    return results.every((result) => result === true);
  }

  /* ----- 2-Pair Promo (WMP-1 / C330) ----- */

  isPairQualifyingItem(item) {
    if (!item || item.gift_card) return false;
    if (item.properties) {
      if ('_gift_product' in item.properties || item.properties._different) return false;
      if (item.properties._children === 'true' || item.properties._children === true) return false;
    }
    const type = (item.product_type || '').trim().toLowerCase();
    return this.pairQualifyingTypes.includes(type);
  }

  countQualifyingPairs(cart) {
    if (!cart || !cart.items || !this.pairQualifyingTypes.length) return 0;
    return cart.items.reduce(
        (count, item) => (this.isPairQualifyingItem(item) ? count + item.quantity : count),
        0,
    );
  }

  isPairPromoActive(cart) {
    if (this.pairPromoMode === 'legacy' || !this.pairQualifyingTypes.length) return false;
    if (!cart || !cart.items) return false;

    const realItems = cart.items.filter(
        (item) =>
            !item.gift_card &&
            !(item.properties && ('_gift_product' in item.properties || item.properties._different)),
    );

    // Empty cart shows pair-promo State 0; carts holding only non-qualifying or
    // gift lines fall back to legacy.
    if (cart.items.length === 0) return true;
    return realItems.length > 0 && this.countQualifyingPairs(cart) >= 1;
  }

  renderPairPromo(cart) {
    const pairCount = this.countQualifyingPairs(cart);
    const state = Math.min(pairCount, 2);
    const pairBars = document.querySelectorAll('[data-pair-promo]');

    document
        .querySelectorAll('.content_body .content:not(.pair-promo), .box-progress-bar .progress:not(.pair_promo)')
        .forEach((el) => el.classList.add('pair-hidden'));
    document
        .querySelectorAll('.empty_cart_line, .content_header .title:not(.content_success_message)')
        .forEach((el) => el.classList.add('pair-hidden'));

    const progress = state === 0 ? 0 : state === 1 ? 50 : 100;
    const message = this.getPairPromoCopy(this.pairPromoTexts[state] || '', state);
    pairBars.forEach((bar) => {
      bar.classList.remove('hide', 'pair-hidden', 'no-show');
      bar.style.display = '';
      bar.classList.toggle('full', state >= 2);
      const label = bar.querySelector('.icon_box .text, .icons span');
      if (label) label.textContent = message;
      const line = bar.querySelector('[data-pair-line]');
      if (line) line.style.width = `${progress}%`;
    });

    document.querySelectorAll('.content_success_message').forEach((mb) => {
      mb.classList.add('pair-active');
      mb.classList.toggle('is-unlocked', state >= 2);
      mb.style.display = message ? 'block' : 'none';
      mb.textContent = message;
    });
    if (message) document.querySelectorAll('.content_header').forEach((header) => header.classList.add('show'));

    // Keep the under-subtotal "Shipping / free" chip aligned with the pair bar:
    // it shows only once 2 qualifying pairs unlock free shipping. Legacy carts
    // never reach here and keep their own dollar/RX chip logic.
    this.updatePairPromoShippingFreeBox(cart, state);
  }

  updatePairPromoShippingFreeBox(cart, pairState = null) {
    const boxes = document.querySelectorAll('.shipping-free-box');
    if (!boxes.length) return;

    const state = pairState == null ? Math.min(this.countQualifyingPairs(cart), 2) : pairState;
    const show = state >= 2;

    boxes.forEach((box) => {
      if (show) {
        box.classList.remove('hide');
        box.style.display = '';
      } else {
        box.classList.add('hide');
        box.style.display = 'none';
      }
    });
  }

  getPairPromoCopy(value, state) {
    const text = String(value || '').trim();
    if (this.pairPromoMode !== 'pair_shipping_case' || state !== 2 || !text || /case/i.test(text)) return text;
    const caseText = String(this.textCartFreeGiftProductText || 'FREE CASE').trim();
    return `${text} + ${caseText || 'FREE CASE'}`;
  }

  resetPairPromoUI() {
    document.querySelectorAll('[data-pair-promo]').forEach((bar) => {
      bar.classList.add('hide');
      bar.classList.remove('full');
      bar.style.display = 'none';
      const line = bar.querySelector('[data-pair-line]');
      if (line) line.style.width = '0%';
    });
    document.querySelectorAll('.pair-hidden').forEach((el) => el.classList.remove('pair-hidden'));
    document
        .querySelectorAll('.content_success_message.pair-active')
        .forEach((el) => el.classList.remove('pair-active', 'is-unlocked'));
  }

  readPairCaseState() {
    try {
      return JSON.parse(sessionStorage.getItem('wmpPairCaseState')) || {};
    } catch (error) {
      return {};
    }
  }

  savePairCaseState(pairs, hasGift) {
    try {
      sessionStorage.setItem('wmpPairCaseState', JSON.stringify({ pairs, hasGift }));
      if (pairs < 2) sessionStorage.removeItem('wmpPairCaseManualRemove');
    } catch (error) {
    }
  }

  /* ----- end 2-Pair Promo ----- */

  async gift_check(cart) {
    if (!cart) return;

    const nonGiftItems = cart.items.filter((item) =>
        !(item.properties && '_gift_product' in item.properties) &&
        !item.gift_card
    );

    const hasPrescriptionProduct = nonGiftItems.some((product) =>
        (product.product_type || '').toLowerCase().includes('prescription')
    );

    const hasRegularProduct = nonGiftItems.some((product) =>
        !(product.product_type || '').toLowerCase().includes('prescription')
    );

    const isMixedCart = hasPrescriptionProduct && hasRegularProduct;

    const gift_card = cart?.items.find((item) => item.gift_card);
    const gift_card_price = gift_card ? gift_card.price * gift_card.quantity : 0;

    const total_price = cart.total_price - gift_card_price;

    const prescriptionGiftID = window.ProductBundlerOptionsConfig.cartPrescriptionGiftProduct;
    const defaultGiftID = this.giftProductID;

    const differentGiftProductID = this.differentGiftProductID;
    const differentGiftIsCart = cart?.items.find((item) => item.variant_id === differentGiftProductID) || false;

    if (!differentGiftIsCart && differentGiftProductID && cart?.total_price >= this.diferentPrice) {
      await this.addGiftProduct(differentGiftProductID, true);
    } else if (differentGiftIsCart && cart?.total_price - differentGiftIsCart.price < this.diferentPrice) {
      await this.removeGiftProduct(differentGiftProductID);
    }

    const prescriptionThreshold =
        this.country === 'us'
            ? window.ProductBundlerOptionsConfig.cartPrescriptionGiftPriceUS
            : window.ProductBundlerOptionsConfig.cartPrescriptionGiftPriceIntl * this.rate;

    const giftIdsTrue = cart.items.some(
        (item) => cart.items.length === 1 && this.enable_card.includes(String(item.product_id)),
    );
    const giftProduct = cart?.items?.find((item) => item.properties?._gift_product);

    if (giftIdsTrue) {
      giftProduct?.id && (await this.removeGiftProduct(giftProduct.id));
      return true;
    } else {
      /* 2-Pair Promo guards: while the pair promo owns the cart, the legacy
         dollar-threshold add/remove logic below must not fight the pair-case. */
      const pairActive = this.isPairPromoActive(cart);
      const pairCount = pairActive ? this.countQualifyingPairs(cart) : 0;

      if (pairActive && this.pairPromoMode === 'pair_shipping_only') {
        // Shipping-only version: no free case line while the pair promo is active.
        this.savePairCaseState(pairCount, false);
        if (giftProduct) {
          return await this.removeGiftProduct(giftProduct.id);
        }
        return true;
      }

      if (pairActive && this.pairPromoMode === 'pair_shipping_case') {
        // Pair mode owns the case while any qualifying frame is in cart.
        // Do not fall through to legacy $100/$110 / RX $200 mixed-cart gift adds
        // (e.g. 1 RX frame + accessory can clear $100 and incorrectly gift a case).
        if (pairCount >= 2) {
          const prev = this.readPairCaseState();
          let manuallyRemoved = false;
          try {
            manuallyRemoved = sessionStorage.getItem('wmpPairCaseManualRemove') === '1';
            if (!manuallyRemoved && prev.pairs >= 2 && prev.hasGift && !giftProduct) {
              // Case disappeared while 2+ pairs held and we didn't remove it: customer did. No re-add loop.
              sessionStorage.setItem('wmpPairCaseManualRemove', '1');
              manuallyRemoved = true;
            }
          } catch (error) {
          }

          this.savePairCaseState(pairCount, !!giftProduct);

          if (!giftProduct && !manuallyRemoved && defaultGiftID) {
            return await this.addGiftProduct(defaultGiftID);
          }
          // Existing case stays put; legacy removal below the $100/$110 dollar
          // threshold is suppressed while 2+ qualifying pairs are in the cart.
          return true;
        }

        this.savePairCaseState(pairCount, false);
        if (giftProduct) {
          return await this.removeGiftProduct(giftProduct.id);
        }
        return true;
      }

      this.savePairCaseState(pairCount, !!giftProduct);

      if (giftProduct) {
        const expectedGiftID =
            (isMixedCart && prescriptionGiftID && total_price >= this.giftPrice)
                ? prescriptionGiftID
                : (hasPrescriptionProduct && !isMixedCart && prescriptionGiftID && total_price >= prescriptionThreshold)
                    ? prescriptionGiftID
                    : (!hasPrescriptionProduct && defaultGiftID && total_price >= this.giftPrice)
                        ? defaultGiftID
                        : null;

        if (expectedGiftID && giftProduct.id !== expectedGiftID) {
          await this.removeGiftProduct(giftProduct.id);
          return await this.addGiftProduct(expectedGiftID);
        } else if (!expectedGiftID) {
          return await this.removeGiftProduct(giftProduct.id);
        }
      }

      if (isMixedCart && total_price >= this.giftPrice && prescriptionGiftID) {
        const alreadyHasPrescriptionGift = giftProduct && giftProduct.id === prescriptionGiftID;
        if (!alreadyHasPrescriptionGift) {
          return await this.addGiftProduct(prescriptionGiftID);
        }

      } else if (hasPrescriptionProduct && total_price >= prescriptionThreshold && prescriptionGiftID) {
        const alreadyHasPrescriptionGift = giftProduct && giftProduct.id === prescriptionGiftID;
        if (!alreadyHasPrescriptionGift) {
          return await this.addGiftProduct(prescriptionGiftID);
        }

      } else if (!hasPrescriptionProduct && total_price >= this.giftPrice && defaultGiftID) {
        const alreadyHasDefaultGift = giftProduct && giftProduct.id === defaultGiftID;
        if (!alreadyHasDefaultGift) {
          return await this.addGiftProduct(defaultGiftID);
        }
      }

      const shouldRemoveGift =
          (isMixedCart && total_price < this.giftPrice) ||
          (hasPrescriptionProduct && !isMixedCart && total_price < prescriptionThreshold) ||
          (!hasPrescriptionProduct && total_price < this.giftPrice);

      if (shouldRemoveGift && giftProduct) {
        return await this.removeGiftProduct(giftProduct.id);
      }

      return true;
    }
    return true;
  }

  async giftLineInit(total_price, hasPrescriptionProduct = false, cart = false, cartGeneration = this.cartStateGeneration) {
    const renderSequence = ++this.pairPromoRenderSequence;
    if (!cart) {
      cart = await window.customAddToCart.getCart();
    }
    if (renderSequence !== this.pairPromoRenderSequence || cartGeneration !== this.cartStateGeneration) return;
    const gift_card = cart?.items.find((item) => item.gift_card);
    const gift_card_price = gift_card ? gift_card.price * gift_card.quantity : 0;
    total_price = total_price - gift_card_price;

    if (this.isPairPromoActive(cart)) {
      this.renderPairPromo(cart);
      return;
    }

    this.resetPairPromoUI();

    if (!hasPrescriptionProduct) {
      if (cart?.items?.length > 0) {
        hasPrescriptionProduct = cart.items.some(
            (item) =>
                !(item.properties && item.properties._gift_product) &&
                (item.product_type || '').toLowerCase().includes('prescription')
        );
      } else {
        hasPrescriptionProduct = false;
      }
    }
    const nonGiftItems = cart.items.filter(
        (item) =>
            !(item.properties && item.properties._gift_product) &&
            !item.gift_card
    );
    const hasRegularProduct = nonGiftItems.some((item) =>
        !(item.product_type || '').toLowerCase().includes('prescription')
    );

    const isMixedCart = hasPrescriptionProduct && hasRegularProduct;

    const header_price = document.querySelector('.content_header');
    const shipping_icon_parent = document.querySelector('.content.free-shipping-us');

    const gift_icon_parent = document.querySelector('.content.gift:not(.empty)');
    const express_icon_parent = document.querySelector('.content.express');
    const prescription_icon_parent = document.querySelector('.content.prescription-gift');

    const content_shipping_line = shipping_icon_parent?.querySelector('span');

    const content_gift_line = gift_icon_parent?.querySelector('span');
    const content_express_line = express_icon_parent?.querySelector('span');
    const content_prescription_line = prescription_icon_parent?.querySelector('span');

    const cart_page_free_two_days_shipping_parent = document.querySelector('.progress.free_two_days_shipping');
    const cart_page_free_two_days_shipping = document.querySelectorAll(
        '.progress.free_two_days_shipping .line span:not(.circle_price)'
    );
    const cart_page_line_shipping_parent = document.querySelector('.progress.free_shipping_progress');
    const drawer_line_shipping_parent = document.querySelector('.content.free-two-days-shipping');
    const drawer_line_free_shipping_parent = document.querySelector('.content.free-shipping-us');
    const drawer_line_free_teo_days_shipping_line = document.querySelector(
        '.content.free-two-days-shipping .line_box span',
    );

    const cart_page_line_shipping = document.querySelector('.progress.free_shipping_progress .line span');
    const cart_page_line_gift_parent = document.querySelector('.progress.free_zipper_case');
    const cart_page_line_gift = document.querySelector('.progress.free_zipper_case .line span');
    const cart_page_line_prescription_parent = document.querySelector('.progress.free_prescription_case');
    const cart_page_line_prescription = document.querySelector('.progress.free_prescription_case .line span');
    const cart_page_line_free_custom_product_parent = document.querySelectorAll('.free_custom_product');
    const cart_page_line_free_custom_product = document.querySelectorAll(
        '.free_custom_product .line span:not(.circle_price)',
    );

    const content_body = document.querySelector('.content_body');
    if (!this.isFreeShippingEnabled) {
      if (shipping_icon_parent) shipping_icon_parent.remove();
      if (cart_page_line_shipping_parent) cart_page_line_shipping_parent.remove();
      if (drawer_line_free_shipping_parent) drawer_line_free_shipping_parent.remove();
    }
    const isUS = this.country === 'us';
    const free2DayEnabledFinal =
        this.free_two_days_shipping_enable &&
        (hasPrescriptionProduct || this.free_two_days_shipping_normal_enable);
    if (hasPrescriptionProduct  && !isMixedCart) {
      if (gift_icon_parent) {
        gift_icon_parent.classList.add('disabled');
        gift_icon_parent.style.display = 'none';

        if (cart_page_line_gift_parent) {
          cart_page_line_gift_parent.classList.add('disabled');
          cart_page_line_gift_parent.style.display = 'none';
        }
      }
    }

    const rate = Number.isFinite(this.rate) && this.rate > 0 ? this.rate : 1;
    const toMinorUnits = (cents) => Math.max(0, Math.round(cents));

    const usShippingThreshold = this.cartFreeShippingUsSpend ?? 0;
    const intlShippingBase = this.cartFreeShippingInternationalSpend ?? usShippingThreshold;

    let shippingThreshold = isUS
        ? usShippingThreshold
        : toMinorUnits(intlShippingBase * rate);

    if (!this.isFreeShippingEnabled) {
      shippingThreshold = null;
    }
    const giftThreshold = this.giftPrice;
    const expressThreshold = this.cartFreeExpressShippingUsSpend;

    const prescriptionThreshold = isUS
        ? (window.ProductBundlerOptionsConfig.cartPrescriptionGiftPriceUS ?? 0)
        : toMinorUnits((window.ProductBundlerOptionsConfig.cartPrescriptionGiftPriceIntl ?? 0) * rate);

    let expressThresholdEffective = expressThreshold;
    if (hasPrescriptionProduct && this.country === 'us') {
      expressThresholdEffective = 1;
    }

    let currency = '$';
    if (header_price?.dataset.currencyCode) {
      currency = this.splitPrice(header_price.dataset.currencyCode)?.currency || '$';
    }

    const setBar = (el, line, progress) => {
      if (!el || !line) return;
      if (el.classList.contains('full')) {
        return;
      }

      const giftProduct = cart?.items?.find((item) => item.properties?._gift_product);

      if (giftProduct && el.classList.contains('prescription-gift')) {
        progress = 100;
      }
      line.style.width = `${progress}%`;

      if (el.classList.contains('free-shipping-us') && cart_page_line_shipping) {
        cart_page_line_shipping.style.width = `${progress}%`;
      } else if (el.classList.contains('gift') && cart_page_line_gift) {
        cart_page_line_gift.style.width = `${progress}%`;
      } else if (el.classList.contains('prescription-gift') && cart_page_line_prescription) {
        cart_page_line_prescription.style.width = `${progress}%`;
      } else if (
          (el.classList.contains('free-two-days-shipping') || el.classList.contains('free_two_days_shipping')) &&
          cart_page_free_two_days_shipping.length
      ) {
        cart_page_free_two_days_shipping.forEach((line) => {
          line.style.width = `${progress}%`;
        });
      }

      if (progress >= 100) {
        if (el.classList.contains('free-shipping-us') && cart_page_line_shipping_parent) {
          cart_page_line_shipping_parent.classList.add('full');
        }

        if (el.classList.contains('gift') && cart_page_line_gift_parent) {
          cart_page_line_gift_parent.classList.add('full');
        }

        if (el.classList.contains('prescription-gift') && cart_page_line_prescription_parent) {
          cart_page_line_prescription_parent.classList.add('full');
        }

        el.classList.add('full');
      } else {
        if (el.classList.contains('free-shipping-us') && cart_page_line_shipping_parent) {
          cart_page_line_shipping_parent.classList.remove('full');
        }

        if (el.classList.contains('gift') && cart_page_line_gift_parent) {
          cart_page_line_gift_parent.classList.remove('full');
        }

        if (el.classList.contains('prescription-gift') && cart_page_line_prescription_parent) {
          cart_page_line_prescription_parent.classList.remove('full');
        }

        el.classList.remove('full');
      }
      el.classList.remove('hide');

      if (cart_page_line_prescription_parent && el.matches('.content.prescription-gift:not(.hide)')) {
        cart_page_line_prescription_parent.classList.remove('disabled');
      }

      el.style.display = '';
    };
    [
      shipping_icon_parent,
      gift_icon_parent,
      express_icon_parent,
      prescription_icon_parent,
      drawer_line_shipping_parent,
      cart_page_free_two_days_shipping_parent
    ].forEach((el) => {
      if (!el) return;

      if (el === express_icon_parent) {
        el.classList.add('hide');
        el.style.display = 'none';
      }

      el.classList.remove('full');

      const span =
          el.querySelector('.line_box span:not(.circle_price)') ||
          el.querySelector('.line span:not(.circle_price)') ||
          el.querySelector('.line span') ||
          el.querySelector('span');

    });
    content_body?.classList.remove('shift');

    const updateShippingFreeBox = () => {
      const shippingFreeBoxes = document.querySelectorAll('.shipping-free-box');

      // 👉 If there's a prescription (RX) — always show
      if (hasPrescriptionProduct) {
        shippingFreeBoxes.forEach((box) => {
          box.classList.remove('hide');
          box.style.display = '';
        });
        return;
      }

      // 👉 Otherwise, use the original logic
      const shippingFreeReached =
          shippingThreshold !== null && total_price >= shippingThreshold;

      shippingFreeBoxes.forEach((box) => {
        if (shippingFreeReached) {
          box.classList.remove('hide');
          box.style.display = '';
        } else {
          box.classList.add('hide');
          box.style.display = 'none';
        }
      });
    };

    const currencySymbols = {
      USD: '$',
      EUR: '€',
      PLN: 'zł',
      GBP: '£',
      SEK: 'kr',
      NOK: 'kr',
      DKK: 'kr',
      CHF: 'CHF',
      CZK: 'Kč',
      HUF: 'Ft',
    };

    const currencyFormat = (value) => {
      const amount = value / 100;
      const formattedAmount = Number.isInteger(amount) ? amount.toString() : amount.toFixed(2);
      const currencyCode = Shopify.currency.active || 'USD';
      const symbol = currencySymbols[currencyCode] || currencyCode;
      return `${symbol}${formattedAmount}`;
    };
    const isPrescriptionFreeCaseEnabled =
        !!window.ProductBundlerOptionsConfig.cartPrescriptionGiftProduct &&
        Number(prescriptionThreshold) > 0 &&
        !!prescription_icon_parent &&
        !prescription_icon_parent.classList.contains('disabled');
    if (hasPrescriptionProduct && this.country !== 'us') {
      if (shipping_icon_parent) {
        shipping_icon_parent.classList.remove('hide');
        shipping_icon_parent.style.display = '';
        shipping_icon_parent.classList.remove('full');
      }

      if (gift_icon_parent) {
        gift_icon_parent.classList.add('hide');
        gift_icon_parent.style.display = 'none';
        gift_icon_parent.classList.remove('full');
      }

      if (prescription_icon_parent) {
        prescription_icon_parent.classList.remove('hide');
        prescription_icon_parent.style.display = '';
        prescription_icon_parent.classList.remove('full');
      }

      if (cart_page_line_prescription_parent) {
        cart_page_line_prescription_parent.classList.remove('hide');
        cart_page_line_prescription_parent.classList.remove('disabled');
        cart_page_line_prescription_parent.style.display = '';
        cart_page_line_prescription_parent.classList.remove('full');
      }

      if (drawer_line_shipping_parent) {
        drawer_line_shipping_parent.classList.add('hide');
        drawer_line_shipping_parent.style.display = 'none';
        drawer_line_shipping_parent.classList.remove('full');
      }

      if (cart_page_free_two_days_shipping_parent) {
        cart_page_free_two_days_shipping_parent.classList.add('hide');
        cart_page_free_two_days_shipping_parent.style.display = 'none';
        cart_page_free_two_days_shipping_parent.classList.remove('full');
      }

      const steps = [
        [shippingThreshold, shipping_icon_parent, content_shipping_line],
        [prescriptionThreshold, prescription_icon_parent, content_prescription_line]
      ];

      for (let i = 0; i < steps.length; i++) {
        const [threshold, el, line] = steps[i];
        if (!threshold || !el || !line) continue;

        const prevThreshold = i === 0 ? 0 : (steps[i - 1][0] || 0);
        const span = Math.max(threshold - prevThreshold, 1);

        let progress = 0;

        if (total_price <= prevThreshold) {
          progress = 0;
        } else if (total_price >= threshold) {
          progress = 100;
        } else {
          progress = ((total_price - prevThreshold) / span) * 100;
        }

        progress = Math.min(Math.max(progress, 0), 100);
        setBar(el, line, progress);
      }

      // Add these circle price elements:
      const shippingCircleDrawer = shipping_icon_parent?.querySelector('.circle_price');
      const prescriptionCircleDrawer = prescription_icon_parent?.querySelector('.circle_price');

      const shippingCirclePage = cart_page_line_shipping_parent?.querySelector('.circle_price');
      const prescriptionCirclePage = cart_page_line_prescription_parent?.querySelector('.circle_price');

      [shippingCircleDrawer, shippingCirclePage].forEach((el) => {
        if (!el) return;
        el.textContent = currencyFormat(shippingThreshold);
        el.classList.remove('hide');
      });

      [prescriptionCircleDrawer, prescriptionCirclePage].forEach((el) => {
        if (!el) return;
        el.textContent = currencyFormat(prescriptionThreshold);
        el.classList.remove('hide');
      });

      const leftToShipping = Math.max(shippingThreshold - total_price, 0);
      const leftToPrescription = Math.max(prescriptionThreshold - total_price, 0);
      const messageBoxes = document.querySelectorAll('.content_success_message');

      let message = '';
      let show = true;
      let unlocked = false;

      const shippingText = this.cartFreeShippingUsText;
      const prescriptionText = this.cartPrescriptionText;

      if (total_price < shippingThreshold) {
        message = `You are ${currencyFormat(leftToShipping)} away from <span>${shippingText}</span>`;
      } else if (isPrescriptionFreeCaseEnabled && total_price < prescriptionThreshold) {
        message = `You are ${currencyFormat(leftToPrescription)} away from <span>${prescriptionText}</span>`;
      } else {
        message = isPrescriptionFreeCaseEnabled
            ? `You've unlocked <span>${shippingText} + ${prescriptionText}</span>`
            : `You've unlocked <span>${shippingText}</span>`;
        unlocked = true;
      }

      if (messageBoxes.length) {
        messageBoxes.forEach((mb) => {
          mb.style.display = show ? 'block' : 'none';
          mb.innerHTML = message;
          mb.classList.toggle('is-unlocked', unlocked);
        });
        if (show) header_price?.classList.add('show');
      }
      updateShippingFreeBox();
      return;
    }
    const isFreeCaseEnabled =
        !!this.giftProductID &&
        Number(this.giftPrice) > 0 &&
        !!gift_icon_parent &&
        !gift_icon_parent.classList.contains('disabled');
    const computeMessage = (forPrescription = false) => {
      const prescriptionReached = total_price >= prescriptionThreshold;
      const shippingReached = total_price >= shippingThreshold;
      const expressUsedThreshold = forPrescription ? expressThresholdEffective : expressThreshold;
      const expressReached = total_price >= expressUsedThreshold;
      const giftReached = total_price >= giftThreshold;

      if (forPrescription) {
        // RX flow: 1) FREE 2-DAY SHIPPING, 2) FREE CASE
        const twoDayThreshold = hasPrescriptionProduct
            ? (this.country === 'us'
                ? this.free_two_days_shipping_price_rx_us
                : this.free_two_days_shipping_price_rx_int * this.rate)
            : this.freeTwoDaysShippingPrice; // 2-day shipping threshold
        const caseThreshold = prescriptionThreshold;           // FREE CASE threshold (RX case)

        const twoDayReached = total_price >= twoDayThreshold;
        const giftProduct = cart?.items?.find((item) => item.properties?._gift_product);
        const caseReached =
            !!giftProduct ||
            total_price >= caseThreshold;

        // Step 1: 2-day not reached yet
        if (!twoDayReached) {
          const leftTo2Day = Math.max(twoDayThreshold - total_price, 0);
          return {
            message: `You are ${currencyFormat(leftTo2Day)} away from <span>${this.freeTwoDaysShippingText}</span>`,
            unlocked: false,
            show: true,
          };
        }

        // Step 2: 2-day reached, case not reached
        if (isPrescriptionFreeCaseEnabled && !caseReached) {
          const leftToCase = Math.max(caseThreshold - total_price, 0);
          return {
            message: `You are ${currencyFormat(leftToCase)} away from <span>${this.cartPrescriptionText}</span>`,
            unlocked: false,
            show: true,
          };
        }

        // Both reached
        const is2DayEnabled =
            this.free_two_days_shipping_enable &&
            (hasPrescriptionProduct || this.free_two_days_shipping_normal_enable);

        return {
          message: is2DayEnabled
              ? (
                  isPrescriptionFreeCaseEnabled
                      ? `You've unlocked <span>${this.freeTwoDaysShippingText} + ${this.cartPrescriptionText}</span>`
                      : `You've unlocked <span>${this.freeTwoDaysShippingText}</span>`
              )
              : (
                  isPrescriptionFreeCaseEnabled
                      ? `You've unlocked <span>${this.cartPrescriptionText}</span>`
                      : ''
              ),
          unlocked: true,
          show: is2DayEnabled || isPrescriptionFreeCaseEnabled,
        };
      }

      if (isUS) {
        if (this.isFreeShippingEnabled && !(total_price >= shippingThreshold)) {
          const leftToShipping = Math.max(shippingThreshold - total_price, 0);
          return {
            message: `You are ${currencyFormat(leftToShipping)} away from <span>${this.cartFreeShippingUsText}</span>`,
            unlocked: false,
            show: true,
          };
        }
        if (isFreeCaseEnabled && !(total_price >= giftThreshold)) {
          const leftToGift = Math.max(giftThreshold - total_price, 0);
          return {
            message: `You are ${currencyFormat(leftToGift)} away from <span>${this.textCartFreeGiftProductText}</span>`,
            unlocked: false,
            show: true,
          };
        }
        if (!(total_price >= expressThreshold)) {

          if (free2DayEnabledFinal && this.country === 'us') {
            const EPS = 1; // 1 cent
            const leftRaw = this.getFree2DayPrice(hasPrescriptionProduct) - total_price;
            if (leftRaw <= EPS) {
              return {
                message: hasPrescriptionProduct
                    ? `You've unlocked <span>${this.freeTwoDaysShippingText} + ${this.textCartFreeGiftProductText}</span>`
                    : `You've unlocked <span>${this.textCartFreeGiftProductText} + ${this.freeTwoDaysShippingText}</span>`,
                unlocked: true,
                show: true,
              };
            }

            const leftTo2day = Math.max(leftRaw, 0);

            return {
              message: `You are ${currencyFormat(leftTo2day)} away from <span>${this.freeTwoDaysShippingText}</span>`,
              unlocked: false,
              show: true,
            };
          }

          const leftToExpress = Math.max(expressThreshold - total_price, 0);

          if (express_icon_parent) {
            return { message: `You are ${currencyFormat(leftToExpress)} away from FREE EXPRESS SHIPPING`, unlocked: false, show: true };
          }

          return {
            message: isFreeCaseEnabled
                ? `You've unlocked <span>${this.cartFreeShippingUsText} + ${this.textCartFreeGiftProductText}</span>`
                : `You've unlocked <span>${this.cartFreeShippingUsText}</span>`,
            unlocked: true,
            show: true,
          };
        }

        if (express_icon_parent) {
          return {
            message: `You've unlocked <span>${this.textCartFreeGiftProductText} + ${this.freeTwoDaysShippingText}</span> `,
            unlocked: true,
            show: true,
          };
        } else {
          return {
            message: free2DayEnabledFinal
                ? `You've unlocked <span>${this.textCartFreeGiftProductText} + ${this.freeTwoDaysShippingText}</span>`
                : `You've unlocked <span>${this.cartFreeShippingUsText} + ${this.textCartFreeGiftProductText}</span>`,
            unlocked: true,
            show: true,
          };
        }
      } else {
        const giftReachedLocal = total_price >= giftThreshold;
        const shippingReachedLocal = total_price >= shippingThreshold;

        if (giftReachedLocal && shippingReachedLocal) {
          return {
            message: isFreeCaseEnabled
                ? `You've unlocked <span>${this.cartFreeShippingUsText} + ${this.textCartFreeGiftProductText}</span>`
                : `You've unlocked <span>${this.cartFreeShippingUsText}</span>`,
            unlocked: true,
            show: true,
          };
        }
        if (!giftReachedLocal && !shippingReachedLocal) {
          const leftToShipping = Math.max(shippingThreshold - (total_price), 0);
          return {
            message: `You are ${currencyFormat(leftToShipping)} away from <span>${this.cartFreeShippingUsText}</span>`,
            unlocked: false,
            show: true,
          };
        }
        if (isFreeCaseEnabled && !giftReachedLocal && shippingReachedLocal) {
          const leftToGift = Math.max(giftThreshold - (total_price), 0);
          return {
            message: `You are ${currencyFormat(leftToGift)} away from ${this.textCartFreeGiftProductText}`,
            unlocked: false,
            show: true,
          };
        }
        if (!isFreeCaseEnabled && shippingReachedLocal) {
          return {
            message: `You've unlocked <span>${this.cartFreeShippingUsText}</span>`,
            unlocked: true,
            show: true,
          };
        }
        if (giftReachedLocal && !shippingReachedLocal) {
          const leftToShipping = Math.max(shippingThreshold - (total_price), 0);
          return { message: `You are ${currencyFormat(leftToShipping)} away from FREE SHIPPING`, unlocked: false, show: true };
        }
        return { message: '', unlocked: false, show: false };
      }
    };

    if (cart_page_line_free_custom_product_parent.length > 0 && cart_page_line_free_custom_product) {
      const differentProgress = Math.min((total_price / this.diferentPrice) * 100, 100);
      cart_page_line_free_custom_product_parent.forEach((item_parent, index) => {
        setBar(item_parent, cart_page_line_free_custom_product[index], differentProgress);
      });
    }

    function multiplyCurrencyString(input, multiplier) {
      const currencyMatch = input.match(/[^\d.,\s]+/g);
      const currency = currencyMatch ? currencyMatch.join('') : '';

      let decimalSeparator = '.';
      if (input.includes(',') && input.lastIndexOf(',') > input.lastIndexOf('.')) {
        decimalSeparator = ',';
      }

      let numericString = input.replace(/[^\d.,-]/g, '').replace(decimalSeparator === ',' ? ',' : '.', '.');

      const value = parseFloat(numericString);
      if (isNaN(value)) return input;

      const result = value * multiplier;

      const formattedNumber = result.toFixed(2).replace('.', decimalSeparator);

      if (input.trim().startsWith(currency)) {
        return input.replace(/[0-9.,-]+/, formattedNumber);
      } else {
        return input.replace(/[0-9.,-]+/, formattedNumber);
      }
    }

    const freeShippingIntPrices = document.querySelectorAll('[data-shipping-price-int]');
    const freeGiftIntPrices = document.querySelectorAll('[data-cart-gift-price-int]');
    const prescriptionIntPrices = document.querySelectorAll('[data-prescription-price-int]');
    const customDifferentProductPrices = document.querySelectorAll('[data-different_product_price_int]');

    const empty_cart_line_us = document.querySelector('.empty_cart_line.line_us');
    const empty_cart_line_int = document.querySelector('.empty_cart_line.line_int');

    if (customDifferentProductPrices.length > 0) {
      customDifferentProductPrices.forEach((customDifferentProductPrice) => {
        const price_num = multiplyCurrencyString(
            customDifferentProductPrice.dataset.different_product_price_int,
            this.rate,
        );
        customDifferentProductPrice.textContent = `${price_num}`;
        customDifferentProductPrice.classList.remove('hide');
      });
    }

    if (freeShippingIntPrices.length > 0) {
      freeShippingIntPrices.forEach((freeShippingIntPrice) => {
        const price_num = multiplyCurrencyString(freeShippingIntPrice.dataset.shippingPriceInt, this.rate);
        freeShippingIntPrice.textContent = `${price_num}`;
        freeShippingIntPrice.classList.remove('hide');
      });
    }

    if (freeGiftIntPrices.length > 0) {
      freeGiftIntPrices.forEach((freeGiftIntPrice) => {
        const price_num = multiplyCurrencyString(freeGiftIntPrice.dataset.cartGiftPriceInt, this.rate);

        freeGiftIntPrice.textContent = `${price_num}`;
        freeGiftIntPrice.classList.remove('hide');
      });
    }

    if (prescriptionIntPrices.length > 0) {
      prescriptionIntPrices.forEach((prescriptionIntPrice) => {
        const price_num = multiplyCurrencyString(prescriptionIntPrice.dataset.prescriptionPriceInt, this.rate);

        prescriptionIntPrice.textContent = `${price_num}`;
        prescriptionIntPrice.classList.remove('hide');
      });
    }

    if (empty_cart_line_us && empty_cart_line_int) {
      if (this.country === 'us') {
        empty_cart_line_us.classList.remove('hide');
        empty_cart_line_int.classList.add('hide');
      } else {
        empty_cart_line_us.classList.add('hide');
        empty_cart_line_int.classList.remove('hide');
      }
    }

    /* START SHOW PROGRESS */
    const drawerItems = document.querySelectorAll('#CartDrawer .content_body .content:not(.hide)');
    setTimeout(() => {
      /*updateItemsVisibility(drawerItems);*/
    })

    setTimeout(() => {
      const pageCartItems = document.querySelectorAll(
          '.box-progress-bar .progress:not(.hide):not(.disabled)',
      );

      if (pageCartItems.length > 0) {
        /*updateItemsVisibility(pageCartItems);*/
      }
    }, 0);

    function updateItemsVisibility(items) {
      items.forEach((item) => item.classList.remove('no-show'));

      for (let i = 0; i < items.length - 1; i++) {
        if (!items[i].classList.contains('full')) {
          items[i + 1].classList.add('no-show');

          if (i + 1 === items.length - 1) {
            items[i + 1].classList.remove('full');
          }
        }
        const lineItem =
            items[i + 1].querySelector('.line_box span:not(.circle_price)') ||
            items[i + 1].querySelector('.line span:not(.circle_price)');

        lineItem.style.borderTopLeftRadius = '0';
        lineItem.style.borderBottomLeftRadius = '0';
      }
    }

    /* END PROGRESS */

    if (hasPrescriptionProduct && this.country === 'us') {
      if (shipping_icon_parent) {
        shipping_icon_parent.remove();
      }

      if (cart_page_line_shipping_parent) {
        cart_page_line_shipping_parent.remove();
      }

      if (drawer_line_free_shipping_parent) {
        drawer_line_free_shipping_parent.remove();
      }
      if (gift_icon_parent) {
        gift_icon_parent.classList.add('hide');
        gift_icon_parent.style.display = 'none';
        gift_icon_parent.classList.remove('full');
      }
      if (prescription_icon_parent) {
        prescription_icon_parent.classList.remove('hide');
        prescription_icon_parent.style.display = '';
        prescription_icon_parent.classList.remove('full');
      }
      if (drawer_line_shipping_parent) {
        drawer_line_shipping_parent.classList.remove('hide');
        drawer_line_shipping_parent.style.display = '';
        drawer_line_shipping_parent.classList.remove('full');
      }
      const freeTwoDaysShippingPrice = hasPrescriptionProduct
          ? (this.country === 'us'
              ? this.free_two_days_shipping_price_rx_us
              : this.free_two_days_shipping_price_rx_int * this.rate)
          : this.freeTwoDaysShippingPrice;
      const circlePrices = document.querySelectorAll(
          '.free-two-days-shipping .circle_price, .free_two_days_shipping .circle_price'
      );

      circlePrices.forEach((el) => {
        el.textContent = currencyFormat(freeTwoDaysShippingPrice);
      });
      const steps = [];

      if (this.country === 'us') {
        steps.push([
          freeTwoDaysShippingPrice,
          drawer_line_shipping_parent,
          drawer_line_free_teo_days_shipping_line
        ]);
      }

      steps.push([
        prescriptionThreshold,
        prescription_icon_parent,
        content_prescription_line
      ]);

      if (this.country === 'us' && cart_page_free_two_days_shipping.length) {
        cart_page_free_two_days_shipping.forEach((line) => {
          steps.splice(1, 0, [freeTwoDaysShippingPrice, cart_page_free_two_days_shipping_parent, line]);
        });
      }
      for (let i = 0; i < steps.length; i++) {
        const [threshold, el, line] = steps[i];
        if (!threshold || !el || !line) continue;

        const prevThreshold = i === 0 ? 0 : (steps[i - 1][0] || 0);
        const span = Math.max(threshold - prevThreshold, 1);

        let progress = 0;

        if (total_price <= prevThreshold) {
          progress = 0;
        } else if (total_price >= threshold) {
          progress = 100;
        } else {
          progress = ((total_price - prevThreshold) / span) * 100;
        }

        progress = Math.min(Math.max(progress, 0), 100);
        setBar(el, line, progress);
      }

      const isRxFlow = hasPrescriptionProduct; // ✅ includes mixed carts
      const { message, show, unlocked } = computeMessage(isRxFlow);
      const messageBoxes = document.querySelectorAll('.content_success_message');
      if (messageBoxes.length) {
        messageBoxes.forEach((mb) => {
          mb.style.display = show ? 'block' : 'none';
          mb.innerHTML = message;
          mb.classList.toggle('is-unlocked', !!unlocked);
        });
        if (show) header_price?.classList.add('show');
      }
      updateShippingFreeBox();
      return;
    }



    // ----- Progressbars & UI: US sequential flow (without prescription) -----
    // ✅ NORMAL PRODUCTS FLOW (shipping → gift → free 2 days)

    if (shipping_icon_parent) {
      shipping_icon_parent.classList.remove('hide');
      shipping_icon_parent.style.display = '';
    }

    if (gift_icon_parent) {
      gift_icon_parent.classList.remove('hide');
      gift_icon_parent.style.display = '';
    }

    if (drawer_line_shipping_parent) {
      if (free2DayEnabledFinal && this.country === 'us') {
        drawer_line_shipping_parent.classList.remove('hide');
        drawer_line_shipping_parent.style.display = '';
      } else {
        drawer_line_shipping_parent.classList.add('hide');
        drawer_line_shipping_parent.style.display = 'none';
      }
    }
    if (cart_page_free_two_days_shipping_parent && this.country === 'us') {
      if (free2DayEnabledFinal) {
        cart_page_free_two_days_shipping_parent.classList.remove('hide');
        cart_page_free_two_days_shipping_parent.style.display = '';
      } else {
        cart_page_free_two_days_shipping_parent.classList.add('hide');
        cart_page_free_two_days_shipping_parent.style.display = 'none';
      }
    }
    const steps = [];

    if (this.isFreeShippingEnabled && shipping_icon_parent) {
      steps.push({
        threshold: shippingThreshold,
        el: shipping_icon_parent,
        line: content_shipping_line
      });
    }

    if (gift_icon_parent) {
      steps.push({
        threshold: giftThreshold,
        el: gift_icon_parent,
        line: content_gift_line
      });
    }

    if (free2DayEnabledFinal && this.country === 'us') {
      if (drawer_line_shipping_parent && drawer_line_free_teo_days_shipping_line) {
        steps.push({
          threshold: this.getFree2DayPrice(hasPrescriptionProduct),
          el: drawer_line_shipping_parent,
          line: drawer_line_free_teo_days_shipping_line,
        });
      }
    }
    for (let i = 0; i < steps.length; i++) {
      const { threshold, el, line } = steps[i];
      if (!threshold || !el || !line) continue;

      const prevThreshold = i === 0
          ? 0
          : (steps[i - 1]?.threshold || 0);
      const span = Math.max(threshold - prevThreshold, 1);
      let progress = 0;

      if (total_price <= prevThreshold) {
        progress = 0;
      } else if (total_price >= threshold) {
        progress = 100;
      } else {
        progress = ((total_price - prevThreshold) / span) * 100;
      }
      progress = Math.min(Math.max(progress, 0), 100);
      setBar(el, line, progress);
    }

    updateShippingFreeBox();
    const { message, show, unlocked } = computeMessage(false);

    const messageBoxes = document.querySelectorAll('.content_success_message');
    const parentContent_body = document.querySelector('.content_body');
    if (messageBoxes.length > 0) {
      messageBoxes.forEach((messageBox) => {
        messageBox.style.display = show ? 'block' : 'none';
        messageBox.classList.toggle('is-unlocked', !!unlocked);
        messageBox.innerHTML = parentContent_body?.classList.contains('full')
            ? message.replace(/\ba FREE CкASE\s*\+\s*/i, '').replace(/\s*\+\s*FREE CASE\s*/i, '')
            : message;
      });

      if (show) header_price?.classList.add('show');
    }
  }

  async addGiftProduct(productID, different = false) {
    if (!productID) return;

    this.beginCartMutation();

    const url = '/cart/add.js';
    const options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: productID,
        quantity: 1,
        properties: different ? { _different: true } : { _gift_product: true },
      }),
    };

    try {
      const response = await fetch(url, options);
      const res = await response.json();

      return !!res.id;
    } catch (error) {
      return false;
    }
  }

  async removeGiftProduct(productID) {
    if (!productID) return;

    this.beginCartMutation();

    try {
      const url = '/cart/update.js';
      const options = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates: {
            [productID]: 0,
          },
        }),
      };

      const response = await fetch(url, options);
      const res = await response.json();

      return !!res.id;
    } catch (error) {
      return false;
    }
  }

  splitPrice(priceString) {
    const match = priceString.match(/([\D]+)(\d+)[.,](\d+)/);
    if (!match) return null;

    const currency = match[1].trim();
    const amountInCents = parseInt(match[2] + match[3]);

    return { currency, amountInCents };
  }

  splitStringPrice(price) {
    if (price.includes('.')) return price.split('.').join('');
    if (price.includes(',')) return price.split(',').join('');
  }
  async updateDrawerDom() {
    try {
      const response = await fetch('/?sections=cart-drawer,cart-icon-bubble');
      const res_new = await response.json();

      if (!res_new) return;

      setTimeout(() => {
        const cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer) return;

        /*        cartDrawer.renderContents({ sections: res_new });*/
        this.moveGiftToFirstPosition();
        /*        this.gift_check();*/
        window.prescriptionModal.init();
      }, 20);
    } catch (error) {
    }
  }

  moveGiftToFirstPosition() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    const cartItemsContainer = cartDrawer.querySelector('.cart-items') || cartDrawer;
    const cartItems = cartItemsContainer.querySelectorAll('.cart-item');

    if (cartItems.length === 0) {
      cartDrawer.classList.add('is-empty');
      return;
    }

    cartDrawer.classList.remove('is-empty');

    const giftItem = Array.from(cartItems).find(
        (item) =>
            item.dataset.giftProduct === 'true' ||
            item.querySelector('[name="properties[_gift_product]"]')?.value === 'true',
    );

    if (giftItem && cartItemsContainer.firstElementChild !== giftItem) {
      giftItem.style.transition = 'all 0.3s ease';
      giftItem.classList.add('moving-gift-item');

      cartItemsContainer.prepend(giftItem);

      setTimeout(() => {
        giftItem.style.transition = '';
        giftItem.classList.remove('moving-gift-item');
      }, 300);
    }
  }

  observeCartItems() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    const cartItemsContainer = cartDrawer.querySelector('.cart-items') || cartDrawer;

    const observer = new MutationObserver(() => {
      this.moveGiftToFirstPosition();
    });

    observer.observe(cartItemsContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-gift-product'],
    });

    document.addEventListener('cart:updated', () => this.moveGiftToFirstPosition());
    window.addEventListener('load', () => this.moveGiftToFirstPosition());
  }
}
window.customAddToCart = new CustomAddToCart();

class PrescriptionModal {
  constructor() {
    this.init();
  }

  init() {
    this.prescriptionModals = document.querySelectorAll('.prescription-modal');
    this.prescriptionElements = document.querySelectorAll('.prescription-modal .prescription-elements');
    this.prescriptionElementsEdit = document.querySelectorAll('.prescription-modal .prescription-elements-edit');

    this.body = document.querySelector('body');
    this.openTrigers = document.querySelectorAll('.prescription-modal .open_btn');
    this.closeTrigers = document.querySelectorAll('.prescription-modal .close-btn');
    this.editTrigers = document.querySelectorAll('.btns-box .btn-edit');
    this.saveTrigers = document.querySelectorAll('.btns-box .btn-save');
    this.backTrigers = document.querySelectorAll('.btns-box .btn-back');

    this.hydratePrescriptionAttributes();
    this.readPrescriptionItems();
    this.initTriggers();
    this.openModal();
    this.editPrescription();
    this.closeModal();

    this.saveNewPrescription();

    this.backTrigers.forEach((triger) => {
      triger.addEventListener('click', (evt) => {
        evt.preventDefault();

        this.resetModals();
      });
    });
    /*		window.cart_drawer_swiper__mobile()*/
  }

  hydratePrescriptionAttributes() {
    this.prescriptionModals.forEach((modal) => {
      const data = parsePrescriptionAttribute(modal.dataset.prescriptionJson);
      if (!data) return;

      modal.prescriptionData = data;

      modal.querySelectorAll('[data-prescription-field]').forEach((field) => {
        const key = field.dataset.prescriptionField;
        const value = data[key];
        const displayValue = this.formatPrescriptionValue(value);

        field.textContent = displayValue;
        field.dataset.name = key;
        field.dataset.value = displayValue === '-' ? '' : String(value);
      });

      this.updatePdVisibility(modal, data);
    });
  }

  formatPrescriptionValue(value) {
    if (value === null || value === undefined || value === '' || value === '-') return '-';
    return String(value);
  }

  updatePdVisibility(modal, data) {
    const hasSinglePd = this.formatPrescriptionValue(data.PD) !== '-';
    const singlePd = modal.querySelector('[data-prescription-single-pd]');

    if (singlePd) {
      singlePd.hidden = !hasSinglePd;
      singlePd.style.display = hasSinglePd ? '' : 'none';
    }

    modal.querySelectorAll('[data-prescription-split-pd]').forEach((field) => {
      const hasValue = !hasSinglePd && this.formatPrescriptionValue(data[field.dataset.prescriptionField]) !== '-';
      field.hidden = !hasValue;
      field.style.display = hasValue ? '' : 'none';
    });
  }

  async saveNewPrescription() {
    this.saveTrigers.forEach((triger, index) => {
      triger.addEventListener('click', async (evt) => {
        evt.preventDefault();
        const target = evt.target;

        const parent = target.closest('.cart-item');
        const parentRemoveBtn = parent.querySelector('cart-remove-button');
        const removeElements = document.querySelectorAll(
            `.cart-item[data-properties-prescription-id="${parentRemoveBtn.dataset.propertiesPrescriptionId}"]`
        );
        const uniqID = +parentRemoveBtn.dataset.propertiesPrescriptionId;

        const { items } = await window.customAddToCart.getCart();

        const productsOld = items.filter((item) => item.properties._uniqueId === uniqID);
        const mainProduct = productsOld.find((item) => item.properties?._main_product);
        const prescriptionKey = mainProduct?.properties?._prescription_key || parentRemoveBtn.dataset.prescriptionKey;

        const newPrescriptionItems = this.prescriptionElementsEdit[index].querySelectorAll('select');
        const newDataProperties = {};

        newPrescriptionItems.forEach((select) => {
          const key = normalizePrescriptionFieldKey(select.dataset.id);

          if (select.value !== '-') {
            newDataProperties[key] = select.value;
          }

          if (select.value === '0.00') {
            newDataProperties[key] = null;
          }
        });


        const data = [];

        const cartItems = target.closest('cart-items') || target.closest('cart-drawer-items');

        if (prescriptionKey) {
          const cart = await window.customAddToCart.getCart();
          const currentPrescriptionData = parsePrescriptionAttribute(cart?.attributes?.[prescriptionKey]) || {};
          const nextPrescriptionData = {
            ...currentPrescriptionData,
            ...newDataProperties
          };

          await updateCartAttributes({
            [prescriptionKey]: JSON.stringify(nextPrescriptionData)
          });

          cartItems?.onCartUpdate?.();
          return;
        }

        productsOld.forEach((product) => {
          if (product.properties._main_product) {
            Object.keys(newDataProperties).forEach((key) => {
              if (key in product.properties) {
                product.properties[key] = newDataProperties[key];
              }
            });
          }

          data.push({
            id: product.id,
            ['variant_id']: product.variant_id,
            properties: { ...product.properties },
            quantity: 1,
          });
        });

        await cartItems.updateQuantityLines(removeElements).then((response) => {
          if (response.ok) {
            setTimeout(() => {
              fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  items: data,
                  sections: ['cart-icon-bubble', 'cart-drawer'],
                }),
              }).then((res) => {
                if (res.ok) {
                  setTimeout(() => {
                    /*                    window.customAddToCart.gift_check();*/
                    /*                    window.customAddToCart.updateDrawerDom();*/
                  }, 100);
                }
              });
            }, 500);
          }
        });
      });
    });
  }

  readPrescriptionItems() {
    const change = new Event('change');

    this.prescriptionElements.forEach((prscription, index) => {
      const selectedElements = prscription.querySelectorAll('.prescription-field');

      selectedElements.forEach((field) => {
        if (field.dataset.name) {
          const editSelects = this.prescriptionElementsEdit[index].querySelectorAll('select');
          editSelects.forEach((select) => {
            if (field.dataset.name === select.dataset.id || field.dataset.name.toLowerCase().includes(select.dataset.id.toLowerCase())) {
              select.value = field.dataset.value;
              select.dispatchEvent(change);
            }
          });
        }
      });
    });
  }

  initTriggers() {
    this.openTrigers.forEach((triger) => (triger.disabled = false));
    this.editTrigers.forEach((triger) => (triger.disabled = false));
  }

  editPrescription() {
    this.editTrigers.forEach((triger, index) => {
      triger.addEventListener('click', (evt) => {
        evt.preventDefault();

        triger.classList.add('hide');
        this.prescriptionElements[index].classList.add('hide');
        this.saveTrigers[index].classList.remove('hide');
        this.backTrigers[index].classList.remove('hide');
        this.prescriptionElementsEdit[index].classList.remove('hide');

        this.readPrescriptionItems();
      });
    });
  }

  closeModal() {
    this.closeTrigers.forEach((closeTriger) => {
      closeTriger.addEventListener('click', () => this.resetModals());
    });
  }

  openModal() {
    this.openTrigers.forEach((openTriger, index) => {
      openTriger.addEventListener('click', (evt) => {
        evt.preventDefault();

        this.initModal(this.prescriptionModals[index]);
      });
    });
  }

  initModal(prescription) {
    const modal = prescription.querySelector('.modal');
    const closeBtn = prescription.querySelector('.close-btn');

    modal.showModal();

    window.addEventListener('keydown', (evt) => {
      if (evt.code === 'Escape') this.resetModals();
    });

    closeBtn.addEventListener('click', (evt) => {
      evt.preventDefault();

      this.body.classList.remove('lock');
      this.resetModals();
      modal.close();
    });

    modal.addEventListener('click', (evt) => {
      const modal = evt.currentTarget;
      const isClickOnBoard = evt.target === modal;

      if (isClickOnBoard) {
        this.body.classList.remove('lock');
        this.resetModals();
        modal.close();
      }
    });
  }

  resetModals() {
    this.prescriptionElements.forEach((element) => element.classList.remove('hide'));
    this.prescriptionElementsEdit.forEach((element) => element.classList.add('hide'));
    this.editTrigers.forEach((element) => element.classList.remove('hide'));
    this.saveTrigers.forEach((element) => element.classList.add('hide'));
    this.backTrigers.forEach((element) => element.classList.add('hide'));
  }
}

window.prescriptionModal = new PrescriptionModal();
